import { NavBar, NavBarContainer } from "@similarweb/ui-components";
import { setSecondaryBarType3, toggleSecondaryBar3 } from "app/actions/secondaryBarActions";
import type { FC } from "react";
import { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { SecondarySideBarContext } from "app/components/sidebar3/secondary-side-bar/SecondarySideBarContext";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import { getSidebarConfig } from "app/components/sidebar3/dynamic-config/getSidebarConfig";
import type { SecondarySideBarType3 } from "../types";

const SecondarySideBarStyled = styled.div.attrs(() => ({
    className: "hidden-print",
}))<{ isPrimarySidebarOpened: boolean }>`
    height: 100%;
    transition: box-shadow 0.75s ease-in-out;
    ${({ isPrimarySidebarOpened }) => isPrimarySidebarOpened && "box-shadow: 6px 0px 15px -4px  rgba(0,0,0,0.10);"}
    ${NavBarContainer} {
        background-color: ${colorsPalettes.carbon[0]};
        border-right: solid 1px ${colorsPalettes.carbon[50]};
    }
`;
export const SecondarySideBar: FC<{ isVisible: boolean }> = ({ isVisible }) => {
    const { params, state } = useCurrentStateAndParams();
    const dispatch = useDispatch();
    const { secondaryBarType3, isPrimarySidebarOpened } = useSelector((state) => {
        const {
            secondaryBar: { secondaryBarType3 },
            primaryBar: { isPrimarySidebarOpened },
        } = state;
        return { secondaryBarType3, isPrimarySidebarOpened };
    });
    // only new states [React pages] has secondaryBarType within the state
    const currentPage = state.name;
    const { selectedAppDataSource } = useSelector((state) => state.appDataSource);
    const context = {
        params,
        currentPage,
        currentModule: proNavigator.getCurrentModule(),
        toggleSecondaryBar: (isOpened: boolean) => dispatch(toggleSecondaryBar3(isOpened)),
    };
    /**
     * The state tree contains states that have a field called "secondaryBarType".
     * this function searches through the state tree, until a relevant state is found
     * and updates the secondary bar type according to the given state.
     */
    const resolveSecondaryBarTypeFromState = useCallback((state: IRouterState): SecondarySideBarType3 | "None" => {
        const stateWithType = proNavigator.getNearestStateWithSecondaryBarType3(state);
        const secondaryBarType = stateWithType?.secondaryBarType3 ?? "None";
        return secondaryBarType;
    }, []);

    useEffect(() => {
        // Upon initial load of the secondary bar, we want to resolve the initial secondary bar type
        // according to the current state. this is done to handle cases where the user refreshed the
        // browser, or accessed a url directly.
        const initialState = proNavigator.current();
        const initialBarType = resolveSecondaryBarTypeFromState(initialState);
        dispatch(setSecondaryBarType3(initialBarType));

        // Whenever a route is updated - resolve the relevant secondary bar type
        // and open/closed state
        // from the navigation state tree.
        const handleStateUpdate = (toState) => {
            const updatedSecondaryBarType = resolveSecondaryBarTypeFromState(toState);
            const isSecondaryBarHidden = !toState.hideSecondaryBar3;
            dispatch(setSecondaryBarType3(updatedSecondaryBarType));
            dispatch(toggleSecondaryBar3(isSecondaryBarHidden));
        };
        handleStateUpdate(state);
    }, [state]);

    const contents = useMemo(() => {
        const sidebarConfig = getSidebarConfig();
        const { secondarySidebarMap } = sidebarConfig;
        return secondarySidebarMap[secondaryBarType3]?.();
    }, [secondaryBarType3, selectedAppDataSource, params]);

    return (
        <SecondarySideBarContext.Provider value={context}>
            <SecondarySideBarStyled isPrimarySidebarOpened={isPrimarySidebarOpened}>
                <NavBar
                    isSidebar3={true}
                    height={"100%"}
                    isVisible={isVisible}
                    getHeaderComponent={() => contents?.SecondaryBarHeader}
                    getBodyComponent={() => contents?.SecondaryBarBody}
                    getFooterComponent={() => contents?.SecondaryBarFooter}
                />
            </SecondarySideBarStyled>
        </SecondarySideBarContext.Provider>
    );
};
